import React, { useEffect, useState, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  BookOpen, ArrowRight, ArrowLeft, Plus, X,
  MoreVertical, Pencil, Trash2, GripVertical, Star,
  EyeOff, Eye, Download, Upload, Settings, Layers, FileText,
  Lock, Unlock, FolderInput, Check, Clock, ClockAlert,
} from 'lucide-react';
import {
  DndContext, closestCenter, PointerSensor,
  useSensor, useSensors, DragEndEvent, DragStartEvent,
  DragOverlay,
} from '@dnd-kit/core';
import {
  SortableContext, useSortable,
  rectSortingStrategy, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import RyanHeader from '../components/RyanHeader';
import RyanHomeHero from '../components/RyanHomeHero';
import RyanSavedFoldersView from '../components/RyanSavedFoldersView';
import RyanFieldPattern from '../components/RyanFieldPattern';
import RyanPwaInstallBanner from '../components/RyanPwaInstallBanner';
import RyanCampaignBanner from '../components/RyanCampaignBanner';
import RyanSubdeckIconPicker from '../components/RyanSubdeckIconPicker';
import { RyanSpace, RyanConfig, RyanSpaceGroupData } from '../types/ryan.types';
import RyanManageSpaceGroupsModal from '../components/RyanManageSpaceGroupsModal';
import { CYCLE_ICONS } from '../../components/dashboard/ExploreContent';
import { RYAN_FIELD_COLORS, RYAN_FIELD_THEMES, getRyanFieldColor } from '../constants/ryanFieldVisuals';
import { RYAN_MEDICAL_ICON_MAP } from '../constants/ryanMedicalIcons';
import { useAuth } from '../../contexts/AuthContext';
import { exportImportService } from '../../services/exportImport.service';
import ImportModal from '../../components/deck_subdeck/ImportModal';
import InterdictedModal from '../../components/AccessControl/InterdictedModal';
import RyanRestrictModal from '../components/RyanRestrictModal';
import './RyanHomePage.css';

const DEFAULT_CONFIG: RyanConfig = {
  whatsappNumber: '5577988362587',
  whatsappMessage: 'Olá! Tenho interesse em adquirir o material {campo}. Poderia me passar mais informações?',
};

const EMPTY_FORM = {
  name: '', description: '', icon: 'BookOpen', colorId: 'floresta', theme: 'default',
  groupId: null as string | null, isFree: false,
};
const NO_GROUP_KEY = '__none__';
const MAX_FAVORITES = 2;

/* ── Skeleton ── */
const SkeletonCard: React.FC = () => (
  <div className="ryan-card-skeleton">
    <div className="ryan-skeleton-top" />
    <div className="ryan-skeleton-body">
      <div className="ryan-skeleton-line" style={{ width: '55%' }} />
      <div className="ryan-skeleton-line" style={{ width: '85%' }} />
      <div className="ryan-skeleton-line" style={{ width: '70%' }} />
    </div>
  </div>
);

/* ── Simple toast ── */
interface ToastMsg { id: number; text: string }
let toastIdCounter = 0;

const ToastContainer: React.FC<{ toasts: ToastMsg[]; onRemove: (id: number) => void }> = ({ toasts, onRemove }) => (
  <div className="ryan-toast-container">
    {toasts.map(t => (
      <div key={t.id} className="ryan-toast" onAnimationEnd={() => onRemove(t.id)}>
        {t.text}
      </div>
    ))}
  </div>
);

/* ── Card base (reutilizado pelo SpaceCard e DragOverlay) ── */
interface CardContentProps {
  space: RyanSpace;
  isAdmin: boolean;
  isFav: boolean;
  cardIndex?: number;
  isDragging?: boolean;
  isOverlay?: boolean;
  openKebabId: string | null;
  setOpenKebabId: (id: string | null) => void;
  onEdit: (space: RyanSpace) => void;
  onDelete: (id: string) => void;
  onToggleFav: (id: string) => void;
  onToggleHidden?: (id: string) => void;
  onToggleBlock?: (id: string) => void;
  onBlockedClick?: (name: string) => void;
  onOpenRestrict?: (space: RyanSpace) => void;
  onRemoveRestriction?: (id: string) => void;
  onRestrictedClick?: (name: string, message: string) => void;
  onExport?: (space: RyanSpace) => void;
  onMove?: (space: RyanSpace) => void;
  dragHandleProps?: object;
  dragAttrProps?: object;
}

const CardContent: React.FC<CardContentProps> = ({
  space, isAdmin, isFav, cardIndex = 0,
  isDragging = false, isOverlay = false,
  openKebabId, setOpenKebabId, onEdit, onDelete, onToggleFav, onToggleHidden, onToggleBlock, onBlockedClick,
  onOpenRestrict, onRemoveRestriction, onRestrictedClick, onExport, onMove,
  dragHandleProps, dragAttrProps,
}) => {
  const navigate = useNavigate();
  // Campo interditado: aluno vê o modal "em obras" em vez de entrar; admin
  // navega normalmente (mesmo padrão de subdeck.isBlocked no RyanFieldPage).
  // "Restringir temporariamente" é checado à parte (estado independente) —
  // qualquer um dos dois já impede o clique; o gate real (não dá pra
  // contornar por URL direta) é no backend, ver GET /spaces/:spaceId.
  const goToSpace = () => {
    if (space.isBlocked && !isAdmin) { onBlockedClick?.(space.name); return; }
    if (space.restricted && !isAdmin) { onRestrictedClick?.(space.name, space.restrictedMessage || ''); return; }
    navigate(`/ryan/campo/${space._id}`);
  };
  const color = getRyanFieldColor(space.colorId);
  // RYAN_MEDICAL_ICON_MAP primeiro (catálogo atual do seletor); CYCLE_ICONS como
  // fallback só pra campos antigos que salvaram um ícone genérico do GRAIA.
  const IconComp = (RYAN_MEDICAL_ICON_MAP[space.icon] || CYCLE_ICONS[space.icon]) as React.ComponentType<any> || BookOpen;
  const decksCount = space.decksCount ?? 0;
  const apostilasCount = space.apostilasCount ?? 0;
  const kebabOpen = openKebabId === space._id;
  const kebabBtnRef = useRef<HTMLButtonElement>(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });

  const openKebab = () => {
    if (kebabBtnRef.current) {
      const rect = kebabBtnRef.current.getBoundingClientRect();
      setMenuPos({
        top: rect.bottom + window.scrollY + 4,
        left: rect.right + window.scrollX - 190,
      });
    }
    setOpenKebabId(kebabOpen ? null : space._id);
  };

  const cardClass = [
    'ryan-space-card',
    isDragging ? 'ryan-space-card--hole' : '',
    isOverlay ? 'ryan-space-card--overlay' : '',
    space.isHidden ? 'ryan-space-card--hidden' : '',
    space.isBlocked ? 'ryan-space-card--blocked' : '',
    space.restricted ? 'ryan-space-card--restricted' : '',
  ].filter(Boolean).join(' ');

  const style: React.CSSProperties = {
    '--card-index': cardIndex,
    '--card-color': color,
    background: color,
  } as React.CSSProperties;

  return (
    <div
      style={style}
      className={cardClass}
      onClick={() => !isDragging && !isOverlay && goToSpace()}
      role={isOverlay ? undefined : 'button'}
      tabIndex={isOverlay ? undefined : 0}
      onKeyDown={e => !isOverlay && e.key === 'Enter' && goToSpace()}
    >
      <div className="ryan-space-card-pattern">
        <RyanFieldPattern theme={space.theme} size="card" />
      </div>

      <div className="ryan-space-card-head">
        <div className="ryan-space-card-icon-wrap">
          <IconComp size={26} className="ryan-space-card-icon" />
        </div>

        <div className="ryan-space-card-title-area">
          <h3 className="ryan-space-card-title">{space.name}</h3>
          {space.isBlocked && (
            <span className="ryan-space-card-blocked-icon" title="Deck interditado">
              <Lock size={13} />
            </span>
          )}
          {space.restricted && (
            <span
              className="ryan-space-card-blocked-icon ryan-space-card-blocked-icon--restricted"
              title={space.restrictedMessage ? `Indisponível temporariamente: ${space.restrictedMessage}` : 'Indisponível temporariamente'}
            >
              <Clock size={13} />
            </span>
          )}
          {isAdmin && space.isHidden && (
            <span className="ryan-space-card-hidden-badge">
              <EyeOff size={10} /> Oculto
            </span>
          )}
        </div>

        <div className="ryan-card-actions" onClick={e => e.stopPropagation()}>
          <button
            className={`ryan-card-fav-btn${isFav ? ' ryan-card-fav-btn--active' : ''}`}
            onClick={() => onToggleFav(space._id)}
            title={isFav ? 'Remover favorito' : 'Favoritar'}
          >
            <Star size={16} />
          </button>
          {isAdmin && (
            <>
              <div
                className="ryan-card-drag-handle"
                {...(dragAttrProps || {})}
                {...(dragHandleProps || {})}
                title="Reordenar"
              >
                <GripVertical size={16} />
              </div>
              <div className="ryan-card-kebab-wrap">
                <button
                  ref={kebabBtnRef}
                  className="ryan-card-kebab-btn"
                  onClick={openKebab}
                >
                  <MoreVertical size={16} />
                </button>
                {kebabOpen && !isOverlay && ReactDOM.createPortal(
                  <div
                    className="ryan-card-kebab-menu"
                    style={{ top: menuPos.top, left: menuPos.left, right: 'auto' }}
                  >
                    <button
                      className="ryan-kebab-item"
                      onClick={() => { setOpenKebabId(null); onEdit(space); }}
                    >
                      <Pencil size={13} /> Editar
                    </button>
                    <button
                      className="ryan-kebab-item"
                      onClick={() => { setOpenKebabId(null); onExport?.(space); }}
                    >
                      <Download size={13} /> Exportar Deck
                    </button>
                    <button
                      className="ryan-kebab-item"
                      onClick={() => { setOpenKebabId(null); onMove?.(space); }}
                    >
                      <FolderInput size={13} /> Mover
                    </button>
                    <button
                      className="ryan-kebab-item"
                      onClick={() => { setOpenKebabId(null); onToggleHidden?.(space._id); }}
                    >
                      {space.isHidden
                        ? <><Eye size={13} /> Mostrar para alunos</>
                        : <><EyeOff size={13} /> Ocultar para alunos</>
                      }
                    </button>
                    <button
                      className="ryan-kebab-item"
                      onClick={() => { setOpenKebabId(null); onToggleBlock?.(space._id); }}
                    >
                      {space.isBlocked
                        ? <><Unlock size={13} /> Remover interdição</>
                        : <><Lock size={13} /> Interditar</>
                      }
                    </button>
                    <button
                      className="ryan-kebab-item"
                      onClick={() => {
                        setOpenKebabId(null);
                        space.restricted ? onRemoveRestriction?.(space._id) : onOpenRestrict?.(space);
                      }}
                      title="Bloqueia o acesso de usuários comuns com uma mensagem sua — admin continua acessando normalmente"
                    >
                      {space.restricted
                        ? <><ClockAlert size={13} /> Remover restrição</>
                        : <><Clock size={13} /> Restringir temporariamente</>
                      }
                    </button>
                    <button
                      className="ryan-kebab-item ryan-kebab-item--danger"
                      onClick={() => { setOpenKebabId(null); onDelete(space._id); }}
                    >
                      <Trash2 size={13} /> Deletar
                    </button>
                  </div>,
                  document.body
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {space.description && (
        <div className="ryan-space-card-body">
          <p className="ryan-space-card-desc">{space.description}</p>
        </div>
      )}

      <div className="ryan-space-card-footer">
        <div className="ryan-space-card-counts">
          {decksCount > 0 && (
            <span className="ryan-space-card-count-item">
              <Layers size={13} /> {decksCount} deck{decksCount !== 1 ? 's' : ''}
            </span>
          )}
          {apostilasCount > 0 && (
            <span className="ryan-space-card-count-item">
              <FileText size={13} /> {apostilasCount} apostila{apostilasCount !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        <button
          className="ryan-space-card-btn"
          onClick={e => { e.stopPropagation(); !isOverlay && goToSpace(); }}
        >
          Entrar <ArrowRight size={14} />
        </button>
      </div>
    </div>
  );
};

/* ── Sortable card wrapper ── */
interface SpaceCardProps {
  space: RyanSpace;
  isAdmin: boolean;
  isFav: boolean;
  cardIndex: number;
  openKebabId: string | null;
  setOpenKebabId: (id: string | null) => void;
  onEdit: (space: RyanSpace) => void;
  onDelete: (id: string) => void;
  onToggleFav: (id: string) => void;
  onToggleHidden?: (id: string) => void;
  onToggleBlock?: (id: string) => void;
  onBlockedClick?: (name: string) => void;
  onOpenRestrict?: (space: RyanSpace) => void;
  onRemoveRestriction?: (id: string) => void;
  onRestrictedClick?: (name: string, message: string) => void;
  onExport?: (space: RyanSpace) => void;
}

const SpaceCard: React.FC<SpaceCardProps> = (props) => {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: props.space._id, disabled: !props.isAdmin });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} className="ryan-space-card-wrap">
      <CardContent
        {...props}
        isDragging={isDragging}
        dragHandleProps={listeners}
        dragAttrProps={attributes}
      />
    </div>
  );
};

/* ── Seção de grupo (uma por RyanSpaceGroup + a implícita "Sem Grupo") ── */
interface RyanSpaceSectionProps {
  title: string;
  isFirst: boolean;
  isAdmin: boolean;
  rawSpaces: RyanSpace[];
  displaySpaces: RyanSpace[];
  favorites: string[];
  sharedCardProps: Omit<SpaceCardProps, 'space' | 'isFav' | 'cardIndex'>;
  activeSpace: RyanSpace | null;
  sensors: ReturnType<typeof useSensors>;
  onDragStart: (event: DragStartEvent) => void;
  onDragEnd: (event: DragEndEvent) => void;
  showSettingsMenu: boolean;
  onToggleSettingsMenu: () => void;
  onCreate: () => void;
  onImport: () => void;
  onManageGroups: () => void;
}

const RyanSpaceSection: React.FC<RyanSpaceSectionProps> = ({
  title, isFirst, isAdmin, rawSpaces, displaySpaces, favorites, sharedCardProps,
  activeSpace, sensors, onDragStart, onDragEnd, showSettingsMenu, onToggleSettingsMenu,
  onCreate, onImport, onManageGroups,
}) => {
  return (
    <>
      <div className="ryan-fields-separator-row" style={!isFirst ? { marginTop: 32 } : undefined}>
        <div className="ryan-fields-separator-line" />
        <span className="ryan-fields-separator-pill">{title}</span>
        <div className="ryan-fields-separator-line" />
        {isAdmin && (
          <div className="ryan-fields-separator-actions">
            <div className="ryan-settings-wrap" onClick={e => e.stopPropagation()}>
              <button className="ryan-icon-action-btn" onClick={onToggleSettingsMenu} title="Configurações">
                <Settings size={15} />
              </button>
              {showSettingsMenu && (
                <div className="ryan-settings-menu">
                  <button onClick={onCreate}><Plus size={14} /> Criar Deck</button>
                  <button onClick={onImport}><Upload size={14} /> Importar Deck</button>
                  <button onClick={onManageGroups}><Settings size={14} /> Gerenciar Grupos</button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {rawSpaces.length === 0 && (
        <div className="ryan-home-empty">
          <BookOpen size={48} color="#0D3B0E" style={{ opacity: 0.3 }} />
          <p>Nenhum deck em "{title}" ainda.</p>
        </div>
      )}

      {displaySpaces.length > 0 && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={isAdmin ? onDragStart : undefined}
          onDragEnd={isAdmin ? onDragEnd : undefined}
        >
          <SortableContext items={displaySpaces.map(s => s._id)} strategy={rectSortingStrategy}>
            <div className="ryan-spaces-grid">
              {displaySpaces.map((space, i) => (
                <SpaceCard
                  key={space._id}
                  space={space}
                  isFav={favorites.includes(space._id)}
                  cardIndex={i}
                  {...sharedCardProps}
                />
              ))}
            </div>
          </SortableContext>

          {/* Clone que segue o cursor durante o arrasto */}
          <DragOverlay dropAnimation={{ duration: 220, easing: 'cubic-bezier(0.23, 1, 0.32, 1)' }}>
            {activeSpace ? (
              <CardContent
                space={activeSpace}
                isFav={favorites.includes(activeSpace._id)}
                cardIndex={0}
                isOverlay={true}
                {...sharedCardProps}
              />
            ) : null}
          </DragOverlay>
        </DndContext>
      )}
    </>
  );
};

type HomeMode = 'spaces' | 'saved';

/* ── Page ── */
const RyanHomePage: React.FC = () => {
  const location = useLocation();
  const { isAdmin, loading: authLoading, user } = useAuth();

  const initMode = (location.state as any)?.mode as HomeMode | undefined;
  const initView = (location.state as any)?.view as string | undefined;
  const initFolderId = (location.state as any)?.folderId as string | undefined;
  // RyanFieldPage redireciona pra cá com isso quando um não-admin tenta
  // acessar um campo interditado direto pela URL (não só pelo card).
  const initBlockedSpaceName = (location.state as any)?.blockedSpaceName as string | undefined;
  // Mesma ideia, pra "Restringir temporariamente" (função independente de
  // isBlocked) — RyanFieldPage redireciona com isso quando o servidor recusa
  // (403) um campo restricted acessado direto pela URL.
  const initRestrictedSpaceName = (location.state as any)?.restrictedSpaceName as string | undefined;
  const initRestrictedSpaceMessage = (location.state as any)?.restrictedSpaceMessage as string | undefined;
  const [homeMode, setHomeMode] = useState<HomeMode>(initMode === 'saved' ? 'saved' : 'spaces');
  const [gridVisible, setGridVisible] = useState(true);
  const [triggerRyanCreate, setTriggerRyanCreate] = useState(false);
  // Texto ao vivo da busca do header — usado só pra trazer o campo pesquisado
  // pra frente da grade (decks/apostilas têm seu próprio dropdown no header).
  const [searchQuery, setSearchQuery] = useState('');

  const [spaces, setSpaces] = useState<RyanSpace[]>([]);
  const [groups, setGroups] = useState<RyanSpaceGroupData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [, setRyanConfig] = useState<RyanConfig>(DEFAULT_CONFIG);
  // Cada seção (um grupo, ou "Sem Grupo") tem seu próprio DndContext — precisa
  // do seu próprio id ativo, indexado pela chave do grupo (_id, ou NO_GROUP_KEY).
  const [activeIdByGroup, setActiveIdByGroup] = useState<Record<string, string | null>>({});
  const [showSettingsMenuByGroup, setShowSettingsMenuByGroup] = useState<Record<string, boolean>>({});
  const [showManageGroupsModal, setShowManageGroupsModal] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editingSpace, setEditingSpace] = useState<RyanSpace | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [openKebabId, setOpenKebabId] = useState<string | null>(null);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [moveTargetSpace, setMoveTargetSpace] = useState<RyanSpace | null>(null);
  const [moving, setMoving] = useState(false);
  const [showInterdictedModal, setShowInterdictedModal] = useState(!!initBlockedSpaceName);
  const [interdictedSpaceName, setInterdictedSpaceName] = useState(initBlockedSpaceName || '');

  // "Restringir temporariamente" — modal admin de configuração (space sendo
  // restringido, ou null = fechado) + modal exibido pra usuário comum que
  // tentou acessar um space restricted (independente do de "Interditado" acima).
  const [restrictingSpace, setRestrictingSpace] = useState<RyanSpace | null>(null);
  const [restrictSpaceSaving, setRestrictSpaceSaving] = useState(false);
  const [restrictedClickInfo, setRestrictedClickInfo] = useState<{ name: string; message: string } | null>(
    initRestrictedSpaceName ? { name: initRestrictedSpaceName, message: initRestrictedSpaceMessage || '' } : null
  );

  /* ── Importar campo (deck completo) ── */
  // Passo 1: seleção do arquivo via ImportModal (drag & drop, padrão compartilhado)
  const [showImportPickerModal, setShowImportPickerModal] = useState(false);
  const [importPickerFiles, setImportPickerFiles] = useState<File[]>([]);
  // Qual seção disparou "Importar campo" — pré-seleciona o grupo certo no
  // passo 2 (importSpaceForm), sem obrigar o admin a trocar o select à toa.
  const [importTargetGroupId, setImportTargetGroupId] = useState<string | null>(null);
  const [importSpaceParsing, setImportSpaceParsing] = useState(false);
  // Passo 2: confirmação de nome/descrição/ícone/cor antes de criar o campo
  const [importSpaceSaving, setImportSpaceSaving] = useState(false);
  const [importSpaceProgress, setImportSpaceProgress] = useState('');
  const [importSpaceFile, setImportSpaceFile] = useState<File | null>(null);
  const [importSpaceForm, setImportSpaceForm] = useState(EMPTY_FORM);
  const [importSpaceError, setImportSpaceError] = useState('');
  const [showImportSpaceModal, setShowImportSpaceModal] = useState(false);

  /* ── Favorites ── */
  const favKey = `ryan_fav_spaces_${user?.userId || user?.email || 'guest'}`;
  const [favorites, setFavorites] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem(favKey) || '[]'); } catch { return []; }
  });

  /* ── Toast ── */
  const [toasts, setToasts] = useState<ToastMsg[]>([]);
  const showToast = (text: string) => {
    const id = ++toastIdCounter;
    setToasts(t => [...t, { id, text }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 2600);
  };

  const handleToggleMode = () => {
    setGridVisible(false);
    setTimeout(() => {
      setHomeMode(m => m === 'spaces' ? 'saved' : 'spaces');
      setGridVisible(true);
    }, 150);
  };

  const toggleFav = (id: string) => {
    setFavorites(prev => {
      let next: string[];
      if (prev.includes(id)) {
        next = prev.filter(x => x !== id);
      } else {
        if (prev.length >= MAX_FAVORITES) {
          showToast('Máximo de 2 decks favoritos atingido');
          return prev;
        }
        next = [...prev, id];
      }
      localStorage.setItem(favKey, JSON.stringify(next));
      return next;
    });
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const loadSpaces = useCallback(async () => {
    const token = localStorage.getItem('accessToken');
    const url = isAdmin ? '/api/ryan/admin/spaces' : '/api/ryan/spaces';
    setLoading(true);
    setError('');
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Falha ao carregar decks');
      setSpaces(await res.json());
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  const loadGroups = useCallback(async () => {
    const token = localStorage.getItem('accessToken');
    const url = isAdmin ? '/api/ryan/admin/space-groups' : '/api/ryan/space-groups';
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setGroups(await res.json());
    } catch { /* falhar aqui não esconde nenhum campo — sectionKeyOf() joga
      qualquer groupId não reconhecido pra "Sem Grupo", nunca some da tela */ }
  }, [isAdmin]);

  useEffect(() => {
    if (authLoading) return;
    loadSpaces();
    loadGroups();
  }, [authLoading, loadSpaces, loadGroups]);

  useEffect(() => {
    const tok = localStorage.getItem('accessToken');
    fetch('/api/ryan/config', { headers: { Authorization: `Bearer ${tok}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setRyanConfig(data); })
      .catch(() => {});
  }, []);

  // Chave de seção de um space — cai em "Sem Grupo" tanto pra groupId:null
  // quanto pra um groupId que aponte pra um grupo que não está (mais) na
  // lista carregada (grupo apagado por outra aba, ou falha momentânea no
  // fetch de /space-groups) — nunca um campo passa a não pertencer a
  // seção nenhuma, o que faria ele sumir da tela.
  const knownGroupIds = new Set(groups.map(g => g._id));
  const sectionKeyOf = (s: RyanSpace) => (s.groupId && knownGroupIds.has(s.groupId)) ? s.groupId : NO_GROUP_KEY;

  // Uma seção (grupo, ou "Sem Grupo") por chamada — cada seção tem seu próprio
  // DndContext, indexado por groupKey em activeIdByGroup.
  const handleDragStartForGroup = (groupKey: string) => (event: DragStartEvent) => {
    setActiveIdByGroup(prev => ({ ...prev, [groupKey]: event.active.id as string }));
  };

  const handleDragEndForGroup = (groupKey: string, rawGroupSpaces: RyanSpace[]) => async (event: DragEndEvent) => {
    setActiveIdByGroup(prev => ({ ...prev, [groupKey]: null }));
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = rawGroupSpaces.findIndex(s => s._id === active.id);
    const newIndex = rawGroupSpaces.findIndex(s => s._id === over.id);
    const reordered = arrayMove(rawGroupSpaces, oldIndex, newIndex);
    setSpaces(prev => [...prev.filter(s => sectionKeyOf(s) !== groupKey), ...reordered]);
    const token = localStorage.getItem('accessToken');
    await fetch('/api/ryan/admin/spaces/reorder', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ spaces: reordered.map((s, i) => ({ _id: s._id, order: i })) }),
    });
  };

  const openCreate = (groupId: string | null = null) => {
    setEditingSpace(null);
    setForm({ ...EMPTY_FORM, groupId });
    setFormError('');
    setShowModal(true);
  };

  const openEdit = (space: RyanSpace) => {
    setEditingSpace(space);
    setForm({
      name: space.name, description: space.description || '', icon: space.icon, colorId: space.colorId,
      theme: space.theme || 'default', groupId: space.groupId ?? null,
      isFree: !!space.isFree,
    });
    setFormError('');
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setEditingSpace(null); setForm(EMPTY_FORM); };

  const handleSave = async () => {
    if (!form.name.trim()) { setFormError('Nome obrigatório'); return; }
    setSaving(true);
    setFormError('');
    try {
      const token = localStorage.getItem('accessToken');
      const url = editingSpace ? `/api/ryan/admin/spaces/${editingSpace._id}` : '/api/ryan/admin/spaces';
      const method = editingSpace ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) { setFormError(data.message || 'Erro ao salvar'); return; }
      closeModal();
      loadSpaces();
    } catch { setFormError('Erro de conexão'); } finally { setSaving(false); }
  };

  const handleDelete = (id: string) => {
    setDeleteTargetId(id);
  };

  const closeDeleteModal = () => setDeleteTargetId(null);

  const confirmDelete = async () => {
    if (!deleteTargetId) return;
    setDeleting(true);
    try {
      const token = localStorage.getItem('accessToken');
      await fetch(`/api/ryan/admin/spaces/${deleteTargetId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      setDeleteTargetId(null);
      loadSpaces();
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleHidden = async (id: string) => {
    const token = localStorage.getItem('accessToken');
    const res = await fetch(`/api/ryan/admin/spaces/${id}/toggle-hidden`, {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      const { isHidden } = await res.json();
      setSpaces(prev => prev.map(s => s._id === id ? { ...s, isHidden } : s));
      showToast(isHidden ? 'Deck ocultado para os alunos' : 'Deck visível para todos');
    }
  };

  const handleToggleBlock = async (id: string) => {
    const token = localStorage.getItem('accessToken');
    const res = await fetch(`/api/ryan/admin/spaces/${id}/toggle-block`, {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      const { isBlocked } = await res.json();
      setSpaces(prev => prev.map(s => s._id === id ? { ...s, isBlocked } : s));
      showToast(isBlocked ? 'Deck interditado!' : 'Interdição removida!');
    }
  };

  // ── "Restringir temporariamente" (independente de Interditar) ──
  const handleConfirmRestrictSpace = async (message: string) => {
    if (!restrictingSpace) return;
    setRestrictSpaceSaving(true);
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`/api/ryan/admin/spaces/${restrictingSpace._id}/restrict`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ restricted: true, message }),
      });
      if (res.ok) {
        const result = await res.json();
        setSpaces(prev => prev.map(s => s._id === restrictingSpace._id
          ? { ...s, restricted: result.restricted, restrictedMessage: result.restrictedMessage }
          : s));
        showToast('Deck restringido temporariamente!');
        setRestrictingSpace(null);
      } else {
        const e = await res.json();
        showToast(e.message || 'Erro ao restringir');
      }
    } finally {
      setRestrictSpaceSaving(false);
    }
  };

  const handleRemoveRestrictionSpace = async (id: string) => {
    const token = localStorage.getItem('accessToken');
    const res = await fetch(`/api/ryan/admin/spaces/${id}/restrict`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ restricted: false }),
    });
    if (res.ok) {
      setSpaces(prev => prev.map(s => s._id === id ? { ...s, restricted: false } : s));
      showToast('Restrição removida — deck normal para todos');
    }
  };

  /* ── Mover deck pra outro grupo ── */
  const closeMoveModal = () => setMoveTargetSpace(null);

  const handleMoveToGroup = async (groupId: string | null) => {
    if (!moveTargetSpace) return;
    setMoving(true);
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`/api/ryan/admin/spaces/${moveTargetSpace._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ groupId }),
      });
      if (!res.ok) { showToast('Erro ao mover deck'); return; }
      const updated = await res.json();
      setSpaces(prev => prev.map(s => s._id === updated._id ? { ...s, groupId: updated.groupId } : s));
      showToast('Deck movido!');
      closeMoveModal();
    } catch {
      showToast('Erro de conexão');
    } finally {
      setMoving(false);
    }
  };

  /* ── Exportar deck (RyanSpace completo) ── */
  const handleExportSpace = async (space: RyanSpace) => {
    if (!space.deckId) {
      showToast('Deck sem conteúdo vinculado — não é possível exportar');
      return;
    }
    try {
      await exportImportService.exportDeck(space.deckId);
      showToast('Deck exportado!');
    } catch {
      showToast('Erro ao exportar deck');
    }
  };

  /* ── Importar deck (RyanSpace completo) ── */
  const closeImportPickerModal = () => {
    setShowImportPickerModal(false);
    setImportPickerFiles([]);
  };

  // Confirmação do ImportModal (passo 1): lê e valida o arquivo, depois abre o
  // modal de metadados (passo 2) já pré-preenchido com nome/descrição do export
  const handleImportPickerConfirm = async () => {
    const file = importPickerFiles[0];
    if (!file) return;

    setImportSpaceParsing(true);
    try {
      // Aceita .json ou .zip (JSON + imagens); as imagens só são validadas/enviadas no passo 2
      const data = await exportImportService.readDeckFile(file);
      if (!data.name || !Array.isArray(data.subdecks)) {
        throw new Error('Arquivo inválido: não é um deck exportado (esperado nome e subdecks)');
      }
      setImportSpaceFile(file);
      setImportSpaceForm({
        name: data.name,
        description: data.description || '',
        icon: 'BookOpen',
        colorId: 'floresta',
        theme: 'default',
        groupId: importTargetGroupId,
        isFree: false,
      });
      setImportSpaceError('');
      closeImportPickerModal();
      setShowImportSpaceModal(true);
    } catch (err: any) {
      showToast(err.message || 'Arquivo JSON inválido');
    } finally {
      setImportSpaceParsing(false);
    }
  };

  const closeImportSpaceModal = () => {
    setShowImportSpaceModal(false);
    setImportSpaceFile(null);
    setImportSpaceForm(EMPTY_FORM);
    setImportSpaceError('');
  };

  const handleConfirmImportSpace = async () => {
    if (!importSpaceFile) return;
    if (!importSpaceForm.name.trim()) { setImportSpaceError('Nome obrigatório'); return; }
    setImportSpaceSaving(true);
    setImportSpaceError('');
    try {
      const result = await exportImportService.importDeckAsRyanSpace(importSpaceFile, {
        name: importSpaceForm.name.trim(),
        description: importSpaceForm.description,
        icon: importSpaceForm.icon,
        colorId: importSpaceForm.colorId,
        theme: importSpaceForm.theme,
        groupId: importSpaceForm.groupId,
        isFree: importSpaceForm.isFree,
      }, setImportSpaceProgress);
      closeImportSpaceModal();
      showToast(
        result.apostilasWithoutContent > 0
          ? `Deck importado! ${result.apostilasWithoutContent} apostila(s) sem conteúdo — reimporte via "Importar apostila"`
          : 'Deck importado com sucesso!'
      );
      loadSpaces();
    } catch (err: any) {
      setImportSpaceError(err.message || 'Erro ao importar deck');
    } finally {
      setImportSpaceSaving(false);
      setImportSpaceProgress('');
    }
  };

  /* ── Sorting / seções ── */
  const searchQueryLower = searchQuery.trim().toLowerCase();
  const sortFn = (a: RyanSpace, b: RyanSpace) => {
    // Campo pesquisado vai pra frente de todos, inclusive favoritos.
    if (searchQueryLower) {
      const aMatch = a.name.toLowerCase().includes(searchQueryLower) ? 0 : 1;
      const bMatch = b.name.toLowerCase().includes(searchQueryLower) ? 0 : 1;
      if (aMatch !== bMatch) return aMatch - bMatch;
    }
    const aFav = favorites.includes(a._id) ? 0 : 1;
    const bFav = favorites.includes(b._id) ? 0 : 1;
    if (aFav !== bFav) return aFav - bFav;
    if (!isAdmin) {
      const aBought = a.comprado ? 0 : 1;
      const bBought = b.comprado ? 0 : 1;
      if (aBought !== bBought) return aBought - bBought;
    }
    return 0;
  };

  // Uma seção por RyanSpaceGroup (na ordem vinda do backend) + uma seção
  // implícita final "Sem Grupo" pra qualquer space com groupId:null.
  const spacesByGroupKey = new Map<string, RyanSpace[]>();
  for (const s of spaces) {
    const key = sectionKeyOf(s);
    if (!spacesByGroupKey.has(key)) spacesByGroupKey.set(key, []);
    spacesByGroupKey.get(key)!.push(s);
  }
  const sections = [
    ...groups.map(g => ({ key: g._id, title: g.name })),
    { key: NO_GROUP_KEY, title: 'Sem Grupo' },
  ].map(({ key, title }) => {
    const rawSpaces = spacesByGroupKey.get(key) || [];
    return { key, title, rawSpaces, displaySpaces: [...rawSpaces].sort(sortFn) };
  });
  // Admin sempre vê toda seção (mesmo vazia, precisa do "+"); aluno comum só
  // vê seções com pelo menos um campo — mesma regra de sempre, generalizada.
  const visibleSections = sections.filter(s => isAdmin || s.rawSpaces.length > 0);

  const deleteTargetSpace = deleteTargetId ? spaces.find(s => s._id === deleteTargetId) : null;

  const sharedCardProps = {
    isAdmin: !!isAdmin,
    openKebabId,
    setOpenKebabId,
    onEdit: openEdit,
    onDelete: handleDelete,
    onToggleFav: toggleFav,
    onToggleHidden: handleToggleHidden,
    onToggleBlock: handleToggleBlock,
    onBlockedClick: (name: string) => { setInterdictedSpaceName(name); setShowInterdictedModal(true); },
    onOpenRestrict: (space: RyanSpace) => setRestrictingSpace(space),
    onRemoveRestriction: handleRemoveRestrictionSpace,
    onRestrictedClick: (name: string, message: string) => setRestrictedClickInfo({ name, message }),
    onExport: handleExportSpace,
    onMove: (space: RyanSpace) => setMoveTargetSpace(space),
  };

  return (
    <div
      className="ryan-home-page"
      onClick={() => { setOpenKebabId(null); setShowSettingsMenuByGroup({}); }}
    >
      {homeMode === 'spaces' && (
        <RyanHeader showLogout={true} onSearchQueryChange={setSearchQuery} />
      )}

      {/* Header próprio e independente do modo "Flashcards Salvos" — não é o
          RyanHeader compartilhado (essa vista deixou de ter a mesma estrutura
          do RyanHomePage antigo, então ganhou um cabeçalho só dela). */}
      {homeMode === 'saved' && (
        <header className="ryan-saved-hero">
          <div className="ryan-saved-hero-inner">
            <button className="ryan-saved-hero-back" onClick={handleToggleMode} title="Decks de Estudo">
              <ArrowLeft size={18} />
            </button>
            <div className="ryan-saved-hero-text">
              <h1>Flashcards Salvos</h1>
              <p>Seus cards organizados em pastas</p>
            </div>
            <button className="ryan-saved-hero-create" onClick={() => setTriggerRyanCreate(true)}>
              <Plus size={15} /> Nova Pasta
            </button>
          </div>
        </header>
      )}

      <ToastContainer toasts={toasts} onRemove={id => setToasts(t => t.filter(x => x.id !== id))} />
      <RyanCampaignBanner />

      <div className="ryan-home-content">
        <RyanPwaInstallBanner />

        {homeMode === 'spaces' && (
          <RyanHomeHero onViewSaved={handleToggleMode} />
        )}

        {/* ── Modo Flashcards Salvos ── */}
        {homeMode === 'saved' && (
          <div className={gridVisible ? 'ryan-grid-fade-in' : 'ryan-grid-fade-out'}>
            <RyanSavedFoldersView
              onShowToast={(msg) => showToast(msg)}
              externalCreateTrigger={triggerRyanCreate}
              onExternalCreateHandled={() => setTriggerRyanCreate(false)}
              initialView={initView === 'all-cards' ? 'all-cards' : initView === 'folder' ? 'folder' : undefined}
              initialFolderId={initFolderId}
            />
          </div>
        )}

        {/* ── Modo Campos de Estudo ── */}
        {homeMode === 'spaces' && (
          <div className={gridVisible ? 'ryan-grid-fade-in' : 'ryan-grid-fade-out'}>
            {loading && (
              <div className="ryan-spaces-grid">
                {[1, 2, 3, 4].map(i => <SkeletonCard key={i} />)}
              </div>
            )}

            {!loading && error && (
              <div className="ryan-home-empty">
                <p style={{ color: '#dc2626' }}>{error}</p>
              </div>
            )}

            {!loading && !error && visibleSections.map((section, i) => (
              <RyanSpaceSection
                key={section.key}
                title={section.title}
                isFirst={i === 0}
                isAdmin={!!isAdmin}
                rawSpaces={section.rawSpaces}
                displaySpaces={section.displaySpaces}
                favorites={favorites}
                sharedCardProps={sharedCardProps}
                activeSpace={
                  activeIdByGroup[section.key] ? spaces.find(s => s._id === activeIdByGroup[section.key]) || null : null
                }
                sensors={sensors}
                onDragStart={handleDragStartForGroup(section.key)}
                onDragEnd={handleDragEndForGroup(section.key, section.rawSpaces)}
                showSettingsMenu={!!showSettingsMenuByGroup[section.key]}
                onToggleSettingsMenu={() => setShowSettingsMenuByGroup(prev => ({ ...prev, [section.key]: !prev[section.key] }))}
                onCreate={() => {
                  setShowSettingsMenuByGroup({});
                  openCreate(section.key === NO_GROUP_KEY ? null : section.key);
                }}
                onImport={() => {
                  setShowSettingsMenuByGroup({});
                  setImportTargetGroupId(section.key === NO_GROUP_KEY ? null : section.key);
                  setShowImportPickerModal(true);
                }}
                onManageGroups={() => {
                  setShowSettingsMenuByGroup({});
                  setShowManageGroupsModal(true);
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modal criar/editar */}
      {showModal && (
        <div className="ryan-modal-overlay" onClick={closeModal}>
          <div className="ryan-modal" onClick={e => e.stopPropagation()}>
            <div className="ryan-modal-header">
              <h3 className="ryan-modal-title">
                {editingSpace ? 'Editar Deck' : 'Criar Deck'}
              </h3>
              <button className="ryan-modal-close-btn" onClick={closeModal}><X size={18} /></button>
            </div>

            <div className="ryan-modal-body">
              <div className="ryan-modal-field">
                <label className="ryan-modal-label">Nome *</label>
                <input
                  className="ryan-modal-input"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Ex: Semiologia Médica"
                  autoFocus
                />
              </div>

              <div className="ryan-modal-field">
                <label className="ryan-modal-label">Descrição</label>
                <textarea
                  className="ryan-modal-textarea"
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Descrição breve do deck…"
                />
              </div>

              <div className="ryan-modal-field">
                <label className="ryan-modal-label">Grupo</label>
                <select
                  className="ryan-modal-select"
                  value={form.groupId ?? ''}
                  onChange={e => setForm(f => ({ ...f, groupId: e.target.value || null }))}
                >
                  <option value="">Sem Grupo</option>
                  {groups.map(g => (
                    <option key={g._id} value={g._id}>{g.name}</option>
                  ))}
                </select>
              </div>

              <div className="ryan-modal-field">
                <label className="ryan-modal-check">
                  <input
                    type="checkbox"
                    checked={form.isFree}
                    onChange={e => setForm(f => ({ ...f, isFree: e.target.checked }))}
                  />
                  Material gratuito (libera estudo sem compra)
                </label>
              </div>

              <div className="ryan-modal-field">
                <label className="ryan-modal-label">Cor</label>
                <div className="ryan-color-grid">
                  {RYAN_FIELD_COLORS.map(c => (
                    <button
                      key={c.id}
                      type="button"
                      className={`ryan-color-dot${form.colorId === c.id ? ' selected' : ''}`}
                      style={{ background: c.color }}
                      onClick={() => setForm(f => ({ ...f, colorId: c.id }))}
                      title={c.label}
                    />
                  ))}
                </div>
              </div>

              <div className="ryan-modal-field">
                <label className="ryan-modal-label">Tema ilustrado</label>
                {(['sistemas', 'provas'] as const).map(group => (
                  <React.Fragment key={group}>
                    <span className="ryan-theme-group-label">
                      {group === 'sistemas' ? 'Sistemas do corpo' : 'Provas'}
                    </span>
                    <div className="ryan-theme-grid">
                      {RYAN_FIELD_THEMES.filter(t => t.group === group).map(t => (
                        <button
                          key={t.id}
                          type="button"
                          className={`ryan-theme-btn${form.theme === t.id ? ' selected' : ''}`}
                          onClick={() => setForm(f => ({ ...f, theme: t.id }))}
                          title={t.label}
                          style={{ background: getRyanFieldColor(form.colorId) }}
                        >
                          <span className="ryan-theme-btn-pattern"><RyanFieldPattern theme={t.id} size="card" /></span>
                          <span className="ryan-theme-btn-label">{t.label}</span>
                        </button>
                      ))}
                    </div>
                  </React.Fragment>
                ))}
              </div>

              <div className="ryan-modal-field">
                <label className="ryan-modal-label">Ícone</label>
                <RyanSubdeckIconPicker
                  value={form.icon}
                  onChange={iconId => setForm(f => ({ ...f, icon: iconId || 'BookOpen' }))}
                />
              </div>

              {formError && <div className="ryan-modal-error">{formError}</div>}
            </div>

            <div className="ryan-modal-footer">
              <button className="ryan-modal-cancel-btn" onClick={closeModal}>Cancelar</button>
              <button className="ryan-modal-save-btn" onClick={handleSave} disabled={saving}>
                {saving ? 'Salvando…' : editingSpace ? 'Salvar' : 'Criar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal confirmar exclusão de campo */}
      {deleteTargetId && (
        <div className="ryan-modal-overlay" onClick={closeDeleteModal}>
          <div className="ryan-modal ryan-modal--danger" onClick={e => e.stopPropagation()}>
            <div className="ryan-modal-header">
              <h3 className="ryan-modal-title">Excluir Deck</h3>
              <button className="ryan-modal-close-btn" onClick={closeDeleteModal}><X size={18} /></button>
            </div>

            <div className="ryan-modal-body">
              <div className="ryan-delete-warning">
                <div className="ryan-delete-warning-icon">
                  <Trash2 size={20} />
                </div>
                <p>
                  Tem certeza que deseja excluir{' '}
                  <strong>{deleteTargetSpace?.name || 'este deck'}</strong>?
                  Todo o conteúdo vinculado (decks e apostilas) será perdido. Esta ação não pode ser desfeita.
                </p>
              </div>
            </div>

            <div className="ryan-modal-footer">
              <button className="ryan-modal-cancel-btn" onClick={closeDeleteModal}>Cancelar</button>
              <button className="ryan-modal-delete-btn" onClick={confirmDelete} disabled={deleting}>
                {deleting ? 'Excluindo…' : 'Excluir'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal mover deck pra outro grupo */}
      {moveTargetSpace && (
        <div className="ryan-modal-overlay" onClick={closeMoveModal}>
          <div className="ryan-modal" onClick={e => e.stopPropagation()}>
            <div className="ryan-modal-header">
              <h3 className="ryan-modal-title">Mover "{moveTargetSpace.name}"</h3>
              <button className="ryan-modal-close-btn" onClick={closeMoveModal}><X size={18} /></button>
            </div>

            <div className="ryan-modal-body">
              <div className="ryan-move-target-list">
                <button
                  className={`ryan-move-target-row${!moveTargetSpace.groupId ? ' ryan-move-target-row--active' : ''}`}
                  onClick={() => handleMoveToGroup(null)}
                  disabled={moving || !moveTargetSpace.groupId}
                >
                  Sem Grupo
                  {!moveTargetSpace.groupId && <Check size={15} />}
                </button>
                {groups.map(g => (
                  <button
                    key={g._id}
                    className={`ryan-move-target-row${moveTargetSpace.groupId === g._id ? ' ryan-move-target-row--active' : ''}`}
                    onClick={() => handleMoveToGroup(g._id)}
                    disabled={moving || moveTargetSpace.groupId === g._id}
                  >
                    {g.name}
                    {moveTargetSpace.groupId === g._id && <Check size={15} />}
                  </button>
                ))}
              </div>
            </div>

            <div className="ryan-modal-footer">
              <button className="ryan-modal-cancel-btn" onClick={closeMoveModal}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: campo interditado ("em obras") */}
      <InterdictedModal
        isOpen={showInterdictedModal}
        onClose={() => setShowInterdictedModal(false)}
        entityType="space"
        entityName={interdictedSpaceName}
      />

      {/* ── Modal: Restringir temporariamente (admin configura) ── */}
      {restrictingSpace && (
        <RyanRestrictModal
          entityName={restrictingSpace.name}
          initialMessage={restrictingSpace.restrictedMessage}
          saving={restrictSpaceSaving}
          onClose={() => setRestrictingSpace(null)}
          onConfirm={handleConfirmRestrictSpace}
        />
      )}

      {/* ── Modal: Conteúdo restrito (usuário comum tentou acessar) — função
          independente de "Interditado" acima, mensagem própria do admin ── */}
      <InterdictedModal
        isOpen={!!restrictedClickInfo}
        onClose={() => setRestrictedClickInfo(null)}
        entityType="space"
        entityName={restrictedClickInfo?.name}
        variant="restricted"
        customMessage={restrictedClickInfo?.message}
      />

      {/* Modal selecionar arquivo do campo a importar (passo 1) */}
      <ImportModal
        isOpen={showImportPickerModal}
        onClose={closeImportPickerModal}
        onImport={handleImportPickerConfirm}
        onFilesSelect={setImportPickerFiles}
        selectedFiles={importPickerFiles}
        type="deck"
        importing={importSpaceParsing}
        acceptZip
      />

      {/* Modal confirmar importação de campo (passo 2: nome/descrição/ícone/cor) */}
      {showImportSpaceModal && (
        <div className="ryan-modal-overlay" onClick={closeImportSpaceModal}>
          <div className="ryan-modal" onClick={e => e.stopPropagation()}>
            <div className="ryan-modal-header">
              <h3 className="ryan-modal-title">Importar Deck</h3>
              <button className="ryan-modal-close-btn" onClick={closeImportSpaceModal}><X size={18} /></button>
            </div>

            <div className="ryan-modal-body">
              <div className="ryan-modal-field">
                <label className="ryan-modal-label">Nome *</label>
                <input
                  className="ryan-modal-input"
                  value={importSpaceForm.name}
                  onChange={e => setImportSpaceForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Ex: Semiologia Médica"
                  autoFocus
                />
              </div>

              <div className="ryan-modal-field">
                <label className="ryan-modal-label">Descrição</label>
                <textarea
                  className="ryan-modal-textarea"
                  value={importSpaceForm.description}
                  onChange={e => setImportSpaceForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Descrição breve do deck…"
                />
              </div>

              <div className="ryan-modal-field">
                <label className="ryan-modal-label">Grupo</label>
                <select
                  className="ryan-modal-select"
                  value={importSpaceForm.groupId ?? ''}
                  onChange={e => setImportSpaceForm(f => ({ ...f, groupId: e.target.value || null }))}
                >
                  <option value="">Sem Grupo</option>
                  {groups.map(g => (
                    <option key={g._id} value={g._id}>{g.name}</option>
                  ))}
                </select>
              </div>

              <div className="ryan-modal-field">
                <label className="ryan-modal-check">
                  <input
                    type="checkbox"
                    checked={importSpaceForm.isFree}
                    onChange={e => setImportSpaceForm(f => ({ ...f, isFree: e.target.checked }))}
                  />
                  Material gratuito (libera estudo sem compra)
                </label>
              </div>

              <div className="ryan-modal-field">
                <label className="ryan-modal-label">Cor</label>
                <div className="ryan-color-grid">
                  {RYAN_FIELD_COLORS.map(c => (
                    <button
                      key={c.id}
                      type="button"
                      className={`ryan-color-dot${importSpaceForm.colorId === c.id ? ' selected' : ''}`}
                      style={{ background: c.color }}
                      onClick={() => setImportSpaceForm(f => ({ ...f, colorId: c.id }))}
                      title={c.label}
                    />
                  ))}
                </div>
              </div>

              <div className="ryan-modal-field">
                <label className="ryan-modal-label">Tema ilustrado</label>
                {(['sistemas', 'provas'] as const).map(group => (
                  <React.Fragment key={group}>
                    <span className="ryan-theme-group-label">
                      {group === 'sistemas' ? 'Sistemas do corpo' : 'Provas'}
                    </span>
                    <div className="ryan-theme-grid">
                      {RYAN_FIELD_THEMES.filter(t => t.group === group).map(t => (
                        <button
                          key={t.id}
                          type="button"
                          className={`ryan-theme-btn${importSpaceForm.theme === t.id ? ' selected' : ''}`}
                          onClick={() => setImportSpaceForm(f => ({ ...f, theme: t.id }))}
                          title={t.label}
                          style={{ background: getRyanFieldColor(importSpaceForm.colorId) }}
                        >
                          <span className="ryan-theme-btn-pattern"><RyanFieldPattern theme={t.id} size="card" /></span>
                          <span className="ryan-theme-btn-label">{t.label}</span>
                        </button>
                      ))}
                    </div>
                  </React.Fragment>
                ))}
              </div>

              <div className="ryan-modal-field">
                <label className="ryan-modal-label">Ícone</label>
                <RyanSubdeckIconPicker
                  value={importSpaceForm.icon}
                  onChange={iconId => setImportSpaceForm(f => ({ ...f, icon: iconId || 'BookOpen' }))}
                />
              </div>

              {importSpaceError && <div className="ryan-modal-error">{importSpaceError}</div>}
            </div>

            <div className="ryan-modal-footer">
              <button className="ryan-modal-cancel-btn" onClick={closeImportSpaceModal}>Cancelar</button>
              <button className="ryan-modal-save-btn" onClick={handleConfirmImportSpace} disabled={importSpaceSaving}>
                {importSpaceSaving ? (importSpaceProgress || 'Importando…') : 'Importar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showManageGroupsModal && (
        <RyanManageSpaceGroupsModal
          groups={groups}
          onClose={() => setShowManageGroupsModal(false)}
          onChange={setGroups}
          onToast={(msg) => showToast(msg)}
        />
      )}
    </div>
  );
};

export default RyanHomePage;
